import config from 'config';

import { addAvgReviewFilterSetting } from './addAvgReviewFilterSetting';
import { addCategoryFilterSetting } from './addCategoryFilterSetting';
import { sizeByKey } from './sizeConstants';
import { sortFilterSettingItems } from './sortFilterSettingItems';
import { getLabelSortedInitialAggregations } from './useLabels';

export function getFilterSettings({
  aggregations,
  region,
  tld,
  gender,
  domain,
}) {
  if (!aggregations?.length) {
    return [];
  }

  const showReviewFilter = config.get('public.brand.features.showReviewFilter');

  let sortedAggregations = getLabelSortedInitialAggregations(
    aggregations,
    region,
    tld,
    gender
  );

  sortedAggregations = sortedAggregations
    ? addCategoryFilterSetting(sortedAggregations)
    : undefined;

  let filterSettings = showReviewFilter
    ? addAvgReviewFilterSetting(sortedAggregations)
    : sortedAggregations;

  filterSettings = sortFilterSettingItems(
    filterSettings,
    'size_shoes',
    (a, b) => (parseFloat(a.value) > parseFloat(b.value) ? 1 : -1)
  );

  // Add custom URL param handlers for custom_color to prevent comma splitting
  filterSettings = filterSettings.map((setting) => {
    if (setting.field === 'custom_color') {
      return {
        ...setting,
        fromUrlParams: ({ filters, urlParams }) => {
          const customColorValue = urlParams.get('custom_color');
          if (customColorValue) {
            // Don't split by comma - treat the entire value as one filter value
            return {
              ...filters,
              aggregationFilter: {
                ...filters.aggregationFilter,
                custom_color: [customColorValue],
              },
            };
          }
          return filters;
        },
        toUrlParams: ({ filters, urlParams, baseFilters }) => {
          const customColorValue = filters.aggregationFilter?.custom_color?.[0];
          const baseCustomColorValue =
            baseFilters.aggregationFilter?.custom_color?.[0];

          // Only add to URL if it exists and is different from base
          if (customColorValue && customColorValue !== baseCustomColorValue) {
            return {
              ...urlParams,
              custom_color: customColorValue,
            };
          }
          return urlParams;
        },
      };
    }
    return setting;
  });

  defaultFilterSettingItemsLabels({ filterSettings, domain });

  return filterSettings;
}

/***
 * This function is used to set the default labels for filter settings.
 * It checks if the filterSettings are provided and if so, it maps through each setting.
 * For each setting, it checks if the field starts with 'size_' and if so, it translates the display name
 * using the sizeByKey function. The translated display name is then set as the label for the item.
 * Finally, it returns the modified filterSettings.
 *
 * @param {Array} filterSettings - The filter settings to be modified.
 * @param {string} domain - The domain to be used for translation.
 * @returns {Array} - The modified filter settings with translated labels.
 */
const defaultFilterSettingItemsLabels = ({ filterSettings, domain }) => {
  if (filterSettings) {
    const profilePreferenceCategoryMapping = config.get(
      'public.brand.profilePreferenceToCategory'
    );
    const sizeFilterTranslation = config.get(
      'public.brand.features.sizeFilterTranslation'
    );
    filterSettings = filterSettings.map((setting) => {
      // support for non-constructor size translations
      const adjustedSetting = { ...setting };
      if (sizeFilterTranslation && setting.field?.startsWith('size_')) {
        const categoryName = profilePreferenceCategoryMapping[setting.field];

        adjustedSetting.items = setting.items?.map((item) => {
          // gender F is currently the only one with full set of mappings until gender M is fully implemented
          const translatedDisplayName = sizeByKey({
            domain,
            gender: 'F',
            category: categoryName,
            key: item.value,
          });
          item.label = translatedDisplayName;
          return item;
        });
      }
      return adjustedSetting;
    });
  }
  return filterSettings;
};
